import React, { useEffect, useState } from 'react';
import { useInventory } from '../../hooks/useInventory';

interface MovementItem {
  id: string;
  createdAt: string;
  consumable?: {
    name: string;
  };
  type: string;
  quantity: string;
  reason: string;
}

/**
 * Issue #346: honor the `?consumableId=<id>` query parameter so the row
 * action "查看流水" on `/inventory/consumables` lands on the matching
 * movement subset. Empty / missing `consumableId` lists every movement in
 * the current org scope — matching the backend contract:
 *
 *   `GET /api/v1/inventory/movements?consumableId=...` returns the full
 *   movement set when no `consumableId` is supplied and a consumable-scoped
 *   subset when it is.
 *
 * The page never reads `tenantId` / `orgId` from the URL — those come from
 * the authenticated session and are enforced by the backend RBAC + scoping.
 */
function readConsumableIdFromUrl(): string {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('consumableId') ?? '';
}

export const InventoryMovements: React.FC = () => {
  const { fetchMovements } = useInventory();
  const [movements, setMovements] = useState<MovementItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [consumableId] = useState<string>(() => readConsumableIdFromUrl());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchMovements(consumableId)
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data)) {
          setMovements(data as MovementItem[]);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : '加载库存流水失败');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [consumableId, fetchMovements]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">库存变动流水</h1>
        {consumableId ? (
          <span
            className="rounded-full bg-blue-100 px-3 py-1 text-xs text-blue-800"
            data-testid="inventory-movements-consumable-filter"
          >
            仅显示耗材 {consumableId} 的流水
          </span>
        ) : (
          <span
            className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-600"
            data-testid="inventory-movements-all-scope"
          >
            显示当前组织全部流水
          </span>
        )}
      </div>

      {loading && <div data-testid="inventory-movements-loading">加载中…</div>}

      {loadError && (
        <p
          className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
          data-testid="inventory-movements-error"
          role="alert"
        >
          {loadError}
        </p>
      )}

      {!loading && !loadError && movements.length === 0 && (
        <div
          className="rounded-md border border-dashed border-gray-300 bg-white px-4 py-10 text-center text-sm text-gray-500"
          data-testid="inventory-movements-empty"
        >
          {consumableId
            ? '该耗材当前没有库存流水记录。'
            : '当前组织下没有任何库存流水记录。'}
        </div>
      )}

      {!loading && !loadError && movements.length > 0 && (
        <div
          className="bg-white shadow rounded-lg overflow-hidden"
          data-testid="inventory-movements-table"
        >
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  时间
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  耗材
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  类型
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  数量
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  原因
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {movements.map((m) => (
                <tr key={m.id} data-testid={`inventory-movement-row-${m.id}`}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(m.createdAt).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {m.consumable?.name ?? '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {m.type === 'in' ? (
                      <span className="text-green-600 font-bold">入库</span>
                    ) : (
                      <span className="text-red-600 font-bold">出库</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {m.quantity}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {m.reason}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
